// TMOS User Approval List Component
// Date: December 19, 2025
// Purpose: List of pending user approval requests with actions

import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { UserApprovalRequest } from '@/services/userApprovalService';
import { UserApprovalCard } from './UserApprovalCard';
import { UserApprovalModal } from './UserApprovalModal';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

interface UserApprovalListProps {
  approvals: UserApprovalRequest[];
  loading: boolean;
  onApprove: () => void;
  onReject: () => void;
  onMerge: () => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const UserApprovalList: React.FC<UserApprovalListProps> = ({
  approvals,
  loading,
  onApprove,
  onReject,
  onMerge,
  page,
  totalPages,
  onPageChange
}) => {
  const { t } = useLanguage();
  const [selectedApproval, setSelectedApproval] = useState<UserApprovalRequest | null>(null);
  const [modalAction, setModalAction] = useState<'approve' | 'reject' | 'merge' | null>(null);

  const handleAction = (approval: UserApprovalRequest, action: 'approve' | 'reject' | 'merge') => {
    setSelectedApproval(approval);
    setModalAction(action);
  };

  const handleModalClose = () => {
    setSelectedApproval(null);
    setModalAction(null);
  };

  const handleModalSuccess = (action: 'approve' | 'reject' | 'merge') => {
    handleModalClose();
    if (action === 'approve') {
      onApprove();
    } else if (action === 'reject') {
      onReject();
    } else {
      onMerge();
    }
  };

  if (loading) {
    return (
      <Card className="p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading') || 'Loading...'}</p>
        </div>
      </Card>
    );
  }

  if (approvals.length === 0) {
    return (
      <Card className="p-8">
        <div className="text-center">
          <p className="text-gray-600 text-lg">
            {t('userApproval.noPendingRequests') || 'No pending approval requests'}
          </p>
          <p className="text-gray-500 text-sm mt-2">
            {t('userApproval.noPendingDescription') || 
              'All user registration requests have been processed.'}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {approvals.map((approval) => (
          <UserApprovalCard
            key={approval.id}
            approval={approval}
            onApprove={() => handleAction(approval, 'approve')}
            onReject={() => handleAction(approval, 'reject')}
            onMerge={() => handleAction(approval, 'merge')}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {t('common.page') || 'Page'} {page} {t('common.of') || 'of'} {totalPages}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onPageChange(page - 1)}
              disabled={page === 1}
            >
              {t('common.previous') || 'Previous'}
            </Button>
            <Button
              variant="outline"
              onClick={() => onPageChange(page + 1)}
              disabled={page === totalPages}
            >
              {t('common.next') || 'Next'}
            </Button>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {selectedApproval && modalAction && (
        <UserApprovalModal
          isOpen={true}
          onClose={handleModalClose}
          approval={selectedApproval}
          action={modalAction}
          onSuccess={() => handleModalSuccess(modalAction)}
        />
      )}
    </>
  );
};

